import { computed, ref } from 'vue';

/**
 * Live session card eligibility — Student Story 25 (Edverum course dashboard), AC-05.
 *
 * The Edverum Assistance Chrome extension is the SOLE signal source for whether a
 * student's device may see the live session card and its QR code. Per AC-05 the
 * card renders only when the extension is installed AND reports that the device is
 * a university-provided (managed) Chromebook. Anything else — not installed, no
 * signal, or "cannot determine" (error/undetermined) — is ineligible, and the
 * card is hidden with no explanatory message (AC-02).
 *
 * The card gate (`policies/TimePeriodPolicy.js` `viewCheckIn`) resolves eligibility through
 * {@link resolveDeviceEligibility}, controlled by {@link STRICT_DEVICE_ELIGIBILITY}. The switch
 * stays false until two things are settled, because the extension reports no managed status
 * today and strict mode would hide the card from every student:
 *   - whether "Edverum Assistance" renames the existing "Stanbridge Classroom"
 *     extension or is a separate one;
 *   - the exact contract the extension uses to report managed-Chromebook status
 *     (attribute/value for the reported state, and for the undetermined state).
 *
 * Until the extension reports managed status, `readExtensionSignal()` returns
 * `managedStatus: null` and the resolver's non-strict mode preserves today's
 * behaviour (any injected device id is treated as eligible).
 */

/** Tri-state result of evaluating the device against AC-05. */
export const DeviceEligibility = Object.freeze({
    /** Extension installed and reports a managed (university-provided) Chromebook. */
    ELIGIBLE: 'eligible',
    /** Extension installed and reports the device is NOT a managed Chromebook. */
    INELIGIBLE: 'ineligible',
    /**
     * Extension missing, emitting no signal, or reporting that managed status
     * cannot be determined. Treated as ineligible per AC-05 — card hidden, no message.
     */
    UNDETERMINED: 'undetermined',
});

/**
 * Managed-device status values the extension may report.
 *
 * `null` (absent) is distinct from `UNKNOWN`: `null` means the extension emitted no
 * managed-status field at all — the current reality, since today's extension only
 * injects a device id — whereas `UNKNOWN` is the extension explicitly saying it
 * could not determine the answer.
 */
export const ManagedChromebookStatus = Object.freeze({
    MANAGED: 'managed',
    UNMANAGED: 'unmanaged',
    UNKNOWN: 'unknown',
});

/**
 * Release gate for Student Story 25, AC-05. While false, an injected device id still counts as
 * eligible (today's behaviour) and AC-05 is NOT enforced. Flip to true once the extension reports
 * managed-Chromebook status; strict mode treats anything but an explicit managed report as ineligible.
 */
export const STRICT_DEVICE_ELIGIBILITY = false;

const DEVICE_ASSET_SELECTOR = 'div[device-asset-id]';

/**
 * Placeholder attribute name for the future managed-status contract. The real
 * name/shape is owned by the extension team (Student Story 25, Q2) — this is the
 * single place to update once it is agreed.
 */
const MANAGED_STATUS_ATTRIBUTE = 'data-managed-chromebook';

/**
 * Normalise a raw managed-status attribute value to a {@link ManagedChromebookStatus}
 * or `null` when the extension reported nothing recognisable.
 *
 * Matching is case-insensitive and trims surrounding whitespace. The accepted
 * token set is speculative until the extension team confirms the real contract
 * (Student Story 25, Q2) — this is the single place to tighten it.
 *
 * @param {string|null} raw
 * @returns {string|null}
 */
export function normaliseManagedStatus(raw) {
    if (typeof raw !== 'string') {
        return null;
    }

    switch (raw.trim().toLowerCase()) {
        case ManagedChromebookStatus.MANAGED:
        case 'true':
            return ManagedChromebookStatus.MANAGED;
        case ManagedChromebookStatus.UNMANAGED:
        case 'false':
            return ManagedChromebookStatus.UNMANAGED;
        case ManagedChromebookStatus.UNKNOWN:
        case 'error':
        case 'undetermined':
            return ManagedChromebookStatus.UNKNOWN;
        default:
            return null;
    }
}

/**
 * Read the raw signal the Edverum Assistance extension injects into the page.
 *
 * Current mechanism (extension internally "Stanbridge Classroom"): a
 * `<div device-asset-id="…">` element is injected into the DOM. There is not yet a
 * managed-Chromebook field, so `managedStatus` is `null` until the contract adds
 * one. This mirrors what `store/Index.js` `deviceAssetId` reads today.
 *
 * @returns {{ deviceAssetId: string|null, managedStatus: string|null }}
 */
export function readExtensionSignal() {
    if (typeof document === 'undefined') {
        return { deviceAssetId: null, managedStatus: null };
    }

    const el = document.querySelector(DEVICE_ASSET_SELECTOR);

    if (!el) {
        return { deviceAssetId: null, managedStatus: null };
    }

    return {
        deviceAssetId: el.getAttribute('device-asset-id') || null,
        managedStatus: normaliseManagedStatus(el.getAttribute(MANAGED_STATUS_ATTRIBUTE)),
    };
}

/**
 * Resolve an extension signal to a tri-state eligibility per AC-05.
 *
 * @param {{ deviceAssetId: string|null, managedStatus: string|null }} signal
 * @param {{ legacyDeviceIdIsEligible?: boolean }} [options]
 *   When `legacyDeviceIdIsEligible` is true (default) and the extension has not
 *   reported managed status, any injected device id resolves to `ELIGIBLE` —
 *   today's behaviour. Set it to false for strict AC-05: only an explicit
 *   managed-Chromebook report is eligible.
 * @returns {string} a {@link DeviceEligibility} value
 */
export function resolveDeviceEligibility(signal, options = {}) {
    const { legacyDeviceIdIsEligible = true } = options;
    const { deviceAssetId = null, managedStatus = null } = signal ?? {};

    if (managedStatus === ManagedChromebookStatus.MANAGED) {
        return DeviceEligibility.ELIGIBLE;
    }

    if (managedStatus === ManagedChromebookStatus.UNMANAGED) {
        return DeviceEligibility.INELIGIBLE;
    }

    if (managedStatus === ManagedChromebookStatus.UNKNOWN) {
        return DeviceEligibility.UNDETERMINED;
    }

    // The extension reported no managed status at all.
    if (deviceAssetId && legacyDeviceIdIsEligible) {
        return DeviceEligibility.ELIGIBLE;
    }

    return DeviceEligibility.UNDETERMINED;
}

/**
 * Composable exposing the current device's live-session eligibility.
 *
 * @param {{ strict?: boolean }} [options]
 *   `strict` (default false) applies AC-05 exactly — only an explicit
 *   managed-Chromebook report is eligible. The default keeps today's device-id
 *   behaviour until the extension contract lands.
 * @returns {{
 *   signal: import('vue').Ref<{ deviceAssetId: string|null, managedStatus: string|null }>,
 *   deviceEligibility: import('vue').ComputedRef<string>,
 *   isDeviceEligible: import('vue').ComputedRef<boolean>,
 *   refresh: () => void,
 * }}
 */
export function useLiveSessionEligibility(options = {}) {
    const { strict = false } = options;

    const signal = ref(readExtensionSignal());

    /**
     * Re-read the extension signal. The initial value is captured when this
     * composable is created, so without a `refresh()` call "first paint wins" —
     * a later inject/remove or a mid-session loss of managed status is not seen.
     * The consuming component is expected to call this on a meaningful boundary
     * (e.g. a `visibilitychange` / `focus` listener it owns and tears down); the
     * exact cadence is a product decision (Student Story 25, Q8).
     */
    const refresh = () => {
        signal.value = readExtensionSignal();
    };

    const deviceEligibility = computed(() =>
        resolveDeviceEligibility(signal.value, { legacyDeviceIdIsEligible: !strict })
    );

    const isDeviceEligible = computed(() => deviceEligibility.value === DeviceEligibility.ELIGIBLE);

    return { signal, deviceEligibility, isDeviceEligible, refresh };
}

export default useLiveSessionEligibility;
